import type { KeyboardEventHandler } from 'react';
import type { FocusableElement } from '@react-types/shared';

/**
 * Checks if a key event is an approved vertical arrow key input
 */
function includesYArrowKeys(val: string): boolean {
  return val === 'ArrowUp' || val === 'ArrowDown';
}

/**
 * Checks if a key event is an approved horizontal arrow key input
 */
function includesXArrowKeys(val: string): boolean {
  return val === 'ArrowLeft' || val === 'ArrowRight';
}

/**
 * Checks if an element is an input element or contained within one
 */
function isAnInputElement(element: HTMLElement): boolean {
  const tagName = element.tagName;

  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element.getAttribute('role') === 'textbox' ||
    // Check if element is within an input's container too
    !!element.closest('input, textarea, [role="textbox"]')
  );
}

/**
 * Utility to ignore keyboard events for specific target elements.
 * Skips handling for:
 * - Input elements: Prevents space, horizontal arrow keys, and modifier key combinations from being handled
 * - Drag handles: Prevents all keys except arrow keys from being handled
 */
export const ignoreInputTargetElements = (
  handler: KeyboardEventHandler<FocusableElement>
) => {
  return (e: React.KeyboardEvent<FocusableElement>) => {
    const target = e.target as HTMLElement;
    const pressedKey = e.key;

    if (isAnInputElement(target)) {
      return;
    }

    // Skip drag handle events except for arrow keys
    if (
      target.closest('[data-drag-handle]') &&
      !includesYArrowKeys(pressedKey) &&
      !includesXArrowKeys(pressedKey)
    ) {
      return;
    }

    handler(e);
  };
};
